<script setup>
import {ref, computed, onMounted, onUnmounted} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {useStore} from 'vuex'

import {PlusCircle} from 'lucide-vue-next'
import MembersList from './MembersList.vue'
import InvitesList from './InvitesList.vue'
import {Button} from '@/components/ui/button'
import {Spinner} from '@/components/ui/spinner'
import DialogAddMember from './DialogAddMember.vue'
import { useCheckProjectAccess } from '@/composables/useCheckAccess'


const store = useStore()
const route = useRoute()
const router = useRouter()

const projectId = computed(() => parseInt(route.params.project_id))
const orgId = computed(() => parseInt(route.params.org_id))
const project = computed(() => store.getters['project/current'])
const members = computed(() => store.getters['project/getMembers'])
const invites = computed(() => store.getters['project/getInvites'])
const notAcceptedInvites = computed(() => invites.value.filter(invite => !invite.is_accepted))

const membersLength = computed(() => members.value.length)
const invitesLength = computed(() => notAcceptedInvites.value.length)

const dialogInviteMember = ref(null)
const isLoading = ref(true)

// Polling for invite updates
const pollingInterval = ref(null)

const startPolling = () => {
  if (!projectId.value) return

  pollingInterval.value = setInterval(async () => {
    if (useCheckProjectAccess(project.value, 'project_invite', ['read'])) {
      await store.dispatch('project/fetchInvites', projectId.value)
    }
  }, 30000) // Poll every 30 seconds
}

const stopPolling = () => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value)
    pollingInterval.value = null
  }
}

onMounted(async () => {
  try {
    // Ensure organization is set from the route
    if (orgId.value) {
      await store.dispatch('org/setSelectedOrg', orgId.value)
    }

    await store.dispatch('project/fetchProject', projectId.value)
    await store.dispatch('project/fetchMembers', projectId.value)

    if (useCheckProjectAccess(project.value, 'project_invite', ['read'])) {
      await store.dispatch('project/fetchInvites', projectId.value)
    }

    startPolling()
  } finally {
    isLoading.value = false
  }
})

onUnmounted(() => {
  stopPolling()
})
</script>

<template>
  <div :key="projectId" data-testid="project-members-page">
    <div v-if="isLoading" class="flex justify-center items-center h-64">
      <Spinner class="w-8 h-8" />
    </div>

    <template v-else>
      <div class="mb-1 flex items-center">
        <div class="basis-1/2">
          <h3 class="text-base font-bold" data-testid="members-count-heading">Members ({{membersLength}})</h3>
        </div>
        <div class="basis-1/2 flex justify-end">
          <DialogAddMember v-if="useCheckProjectAccess(project, 'project_invite', ['create'])" />
        </div>
      </div>

      <div>
        <MembersList :members="members" data-testid="current-members-list" />
      </div>

      <div class="mt-6 mb-2 flex items-center">
        <div class="basis-1/2">
          <h3 class="text-base font-bold" data-testid="pending-invites-heading">Pending invites ({{invitesLength}})</h3>
        </div>
      </div>
      <InvitesList v-if="invitesLength" :invites="notAcceptedInvites" :key="invitesLength" data-testid="pending-invites-list" />
    </template>
  </div>
</template>
